import { useEffect, useRef, useState } from 'react';
import { api } from '../api.js';
import { useConfirm } from './ConfirmDialog.jsx';
import { OPEN_ADD_TANK_EVENT, HYDROS_CHANGED_EVENT } from '../app-open.js';
import {
  NEW_TANK,
  keyedControllers,
  presetKeyTank,
  asksKeyTank,
  keyTankOptions,
  keyAddedMessage,
  linkedNote,
} from '../hydros-keys.js';

// Account-level HYDROS controls — connect the CoralVue login once,
// disconnect it once. Lives in the account menu (TankSheet), NOT in
// every tank's Equipment panel: the account is per-user (one login,
// many controllers), so its connect/disconnect belongs in one place.
// Per-tank controller selection stays in EquipmentSection/HydrosLinkPanel.

export function HydrosAccountPanel({ focus = false, linkTo = null } = {}) {
  const confirm = useConfirm();
  const [account, setAccount] = useState(null); // null = loading
  const [form, setForm] = useState(null); // { username, password } when open
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');

  const reload = async () => {
    try {
      setAccount(await api('/api/hydros'));
    } catch (e) {
      setAccount({ connected: false, unavailable: true });
      setErr(e.status === 503 ? '' : e.message || 'Could not load HYDROS status.');
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // After a key, link or unlink: Home's Today asks again what is unlinked.
  const changed = async () => {
    await reload();
    window.dispatchEvent(new Event(HYDROS_CHANGED_EVENT));
  };

  const connect = async () => {
    if (!form?.username?.trim() || !form?.password) {
      setErr('Enter your HYDROS email and password.');
      return;
    }
    setBusy('connect');
    setErr('');
    try {
      await api('/api/hydros/connect', {
        method: 'POST',
        body: { username: form.username.trim(), password: form.password },
      });
      setForm(null);
      await reload();
    } catch (e) {
      setErr(
        e.payload?.detail ||
          (e.status === 503
            ? 'HYDROS linking is not enabled on this server yet.'
            : e.message || 'Could not sign in to HYDROS.')
      );
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async () => {
    // Tanks still read through the password link go dark when it goes: name
    // them, so a keeper moving to keys re-links them first.
    const dark = (account?.controllers || [])
      .filter((c) => c.source !== 'api' && c.assigned_tank_name)
      .map((c) => c.assigned_tank_name);
    const ok = await confirm({
      title: 'Disconnect your HYDROS account?',
      body: dark.length
        ? `${dark.join(', ')} still ${dark.length > 1 ? 'read' : 'reads'} HYDROS through this password link and will stop getting readings. Link ${dark.length > 1 ? 'them' : 'it'} to a key-connected controller first. Your CoralVue HYDROS account itself is unaffected.`
        : 'Every tank using a controller from this account loses its live data until you reconnect. Your CoralVue HYDROS account itself is unaffected.',
      confirmLabel: 'Disconnect',
      destructive: true,
    });
    if (!ok) return;
    setBusy('disconnect');
    setErr('');
    try {
      await api('/api/hydros', { method: 'DELETE' });
      await reload();
    } catch (e) {
      setErr(e.message || 'Could not disconnect.');
    } finally {
      setBusy(null);
    }
  };

  if (account === null) {
    return (
      <div className="ts-add-form" style={{ marginTop: 12 }}>
        <p className="ts-hint">Checking HYDROS…</p>
      </div>
    );
  }
  // 503 / unconfigured server: hide the panel entirely rather than show a
  // dead control.
  if (account.unavailable) return null;

  const keys = account.api?.keys || [];
  const keysOn = Boolean(account.api?.available) || keys.length > 0;
  const passwordOn = Boolean(account.password_connected);
  // With keys on, the password form is not offered: CoralVue is ending
  // password sign-ins for other apps. A keeper already linked by password
  // keeps it until they add a key and remove it.
  const showPasswordBlock = passwordOn || !keysOn;

  return (
    <div className="ts-add-form" style={{ marginTop: 12 }}>
      <div className="ts-add-head">
        <h4 className="font-heading">Connect HYDROS</h4>
      </div>

      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}

      {keysOn && (
        <HydrosKeysSection
          keys={keys}
          controllers={account.controllers || []}
          canAdd={Boolean(account.api?.available)}
          onChanged={changed}
          startOpen={focus}
        />
      )}

      {showPasswordBlock && (
        <PasswordLink
          account={account}
          keysOn={keysOn}
          form={form}
          setForm={setForm}
          busy={busy}
          connect={connect}
          disconnect={disconnect}
        />
      )}

      {account.connected && (
        <HydrosControllersList account={account} onChanged={changed} linkTo={linkTo} />
      )}
    </div>
  );
}

// The older link: the keeper's HYDROS email and password, kept encrypted.
function PasswordLink({ account, keysOn, form, setForm, busy, connect, disconnect }) {
  if (account.password_connected) {
    return (
      <div className="hk-block">
        <div className="hk-title">Password link</div>
        <p className="ts-hint" style={{ marginTop: 4 }}>
          Linked as {account.account_label}.
          {keysOn
            ? ' CoralVue is ending password sign-ins for apps like TankSensei. Add a key above (one per controller, or one for a whole Collective), check each tank\'s HYDROS panel says "Connected with a HYDROS key", then remove the password link.'
            : ''}
        </p>
        <button
          type="button"
          className="hk-btn hk-btn-danger"
          onClick={disconnect}
          disabled={busy === 'disconnect'}
        >
          {busy === 'disconnect' ? 'Removing…' : 'Remove password link'}
        </button>
      </div>
    );
  }
  if (form) {
    return (
      <>
        <div className="ts-row-body" style={{ marginTop: 10 }}>
          <label className="ts-field" style={{ minWidth: 180, flex: '1 1 180px' }}>
            <span>HYDROS email</span>
            <input
              type="email"
              autoComplete="off"
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
            />
          </label>
          <label className="ts-field" style={{ minWidth: 160, flex: '1 1 160px' }}>
            <span>Password</span>
            <input
              type="password"
              autoComplete="off"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
            />
          </label>
        </div>
        <div className="ts-row-body">
          <button type="button" className="ts-save" onClick={connect} disabled={busy === 'connect'}>
            {busy === 'connect' ? 'Signing in…' : 'Connect'}
          </button>
          <button type="button" className="ts-chip" onClick={() => setForm(null)}>
            Cancel
          </button>
        </div>
        <p className="ts-hint" style={{ marginTop: 6 }}>
          Same login as the HYDROS app. Stored encrypted — used only to read your controller data.
          Connect once; assign a controller to each tank in its Equipment settings.
        </p>
      </>
    );
  }
  return (
    <>
      <p className="ts-hint" style={{ marginTop: 8 }}>
        Connect your CoralVue HYDROS account once. Then pick which controller drives each tank from
        that tank's Equipment settings.
      </p>
      <button
        type="button"
        className="ts-add-btn"
        onClick={() => setForm({ username: '', password: '' })}
      >
        Link HYDROS account
      </button>
    </>
  );
}

function agoText(iso) {
  if (!iso) return null;
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} days ago`;
}

function keyLine(k) {
  if (k.status === 'key_rejected' || k.status === 'device_gone') return k.message;
  if (k.status === 'error') return k.message || 'HYDROS did not answer the last check.';
  const seen = agoText(k.last_synced_at);
  if (!k.online) {
    return `Key works, but the controller has sent no probe readings in the last half hour${
      seen ? ` (checked ${seen})` : ''
    }.`;
  }
  return `Reading${seen ? `, last checked ${seen}` : ''}.`;
}

// Each aquarium is its own tank: Add a tank opens over the app, and Account
// comes back on Connect HYDROS after it with the new tank picked for this
// controller, one tap from linked.
function openAddTankForHydros(thingId) {
  window.dispatchEvent(
    new CustomEvent(OPEN_ADD_TANK_EVENT, {
      detail: { returnTo: 'hydros-link', thingId: thingId || null },
    })
  );
}

// Device keys for CoralVue's official API: one per controller or
// Collective, made in the HYDROS app (the /hydros guide shows where).
function HydrosKeysSection({ keys, controllers, canAdd, onChanged, startOpen = false }) {
  const confirm = useConfirm();
  // Arriving by /app/connect/hydros opens the key field ready to paste.
  const [open, setOpen] = useState(Boolean(startOpen && canAdd));
  const [key, setKey] = useState('');
  const [label, setLabel] = useState('');
  // Which tank the key is for (hydros-keys.js): the only tank without
  // asking, unless it already reads a controller through a key of its own
  // or the keeper says the key is for another aquarium; asked otherwise.
  const [tanks, setTanks] = useState(null);
  const [tankId, setTankId] = useState('');
  const [otherAquarium, setOtherAquarium] = useState(false);
  const keyed = keyedControllers(keys);
  const names = new Map([
    ...keys.map((k) => [k.device_id, k.label || k.device_name]),
    ...(controllers || []).map((c) => [c.thing_id, c.name]),
  ]);
  const keysRef = useRef(keys);
  keysRef.current = keys;
  useEffect(() => {
    if (!open || tanks) return;
    api('/api/tanks')
      .then((list) => {
        const own = Array.isArray(list) ? list.filter((t) => !t.role || t.role === 'owner') : [];
        setTanks(own);
        setTankId(presetKeyTank(own, keyedControllers(keysRef.current)));
        setOtherAquarium(false);
      })
      .catch(() => setTanks([]));
  }, [open, tanks]);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(null); // { text, addTank, thingId }
  const asking = Boolean(tanks) && asksKeyTank(tanks, keyed, otherAquarium);

  const add = async () => {
    if (!key.trim()) {
      setErr('Paste the key from the HYDROS app.');
      return;
    }
    if (asking && !tankId) {
      setErr('Pick the tank this key is for.');
      return;
    }
    setBusy('add');
    setErr('');
    setDone(null);
    try {
      const out = await api('/api/hydros/keys', {
        method: 'POST',
        body: {
          key: key.trim(),
          label: label.trim() || undefined,
          tank_id: tankId && tankId !== NEW_TANK ? tankId : undefined,
        },
      });
      setKey('');
      setLabel('');
      setOpen(false);
      setDone({
        ...keyAddedMessage(out, { tanks, choice: tankId }),
        thingId: out?.key?.device_id || null,
      });
      // The tank may read this controller now: the next key must see that,
      // and must not inherit this key's pick.
      setTanks(null);
      setTankId('');
      setOtherAquarium(false);
      await onChanged();
    } catch (e) {
      setErr(e.payload?.detail || e.message || 'Could not add the key.');
    } finally {
      setBusy(null);
    }
  };

  const remove = async (k) => {
    const name = k.label || k.device_name || 'this controller';
    const ok = await confirm({
      title: `Remove the key for ${name}?`,
      body: 'TankSensei stops reading this controller. Readings already logged stay in your tanks. To cut access on the HYDROS side as well, delete the key in the HYDROS app.',
      confirmLabel: 'Remove key',
      destructive: true,
    });
    if (!ok) return;
    setBusy(k.id);
    setErr('');
    try {
      await api(`/api/hydros/keys/${k.id}`, { method: 'DELETE' });
      await onChanged();
    } catch (e) {
      setErr(e.message || 'Could not remove the key.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="hk-block">
      <div className="hk-title">HYDROS keys</div>
      <p className="ts-hint" style={{ marginTop: 4 }}>
        Make a <strong>Read only</strong> key in the HYDROS app for each controller or Collective,
        with TankSensei as the provider, and paste it here. TankSensei only reads: it never changes
        an output or a mode. You can delete the key in the HYDROS app at any time.{' '}
        <a href="/hydros#setup" target="_blank" rel="noopener">
          How do I get a key?
        </a>
      </p>

      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      {done && (
        <div className="hk-done" role="status">
          <p style={{ margin: 0 }}>{done.text}</p>
          {done.addTank && (
            <button
              type="button"
              className="hk-btn hk-btn-primary"
              style={{ marginTop: 8 }}
              onClick={() => openAddTankForHydros(done.thingId)}
            >
              Add a tank
            </button>
          )}
        </div>
      )}

      {keys.length > 0 && (
        <ul className="hk-list">
          {keys.map((k) => (
            <li key={k.id} className="hk-row">
              <div className="hk-row-main">
                <div className="hk-name">
                  {k.label || k.device_name || 'HYDROS controller'}
                  {k.device_type && <span className="hk-type"> · {k.device_type}</span>}
                </div>
                <div
                  className={`ts-hint${
                    k.status === 'key_rejected' || k.status === 'device_gone' ? ' hk-bad' : ''
                  }`}
                >
                  {keyLine(k)}
                </div>
              </div>
              <button
                type="button"
                className="hk-btn hk-btn-danger"
                onClick={() => remove(k)}
                disabled={busy === k.id}
              >
                {busy === k.id ? 'Removing…' : 'Remove'}
              </button>
            </li>
          ))}
        </ul>
      )}

      {canAdd &&
        (open ? (
          <div className="hk-form">
            <label className="hk-field">
              <span>Key from the HYDROS app</span>
              <input
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="Paste the key"
                autoFocus={startOpen}
              />
            </label>
            {asking && (
              <label className="hk-field">
                <span>Which tank is this key for?</span>
                <select value={tankId} onChange={(e) => setTankId(e.target.value)}>
                  <option value="">Pick a tank…</option>
                  {keyTankOptions(tanks, names).map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <small className="ts-hint">
                  {tankId === NEW_TANK
                    ? 'Each aquarium is its own tank in TankSensei. Add the key now, then add the tank and link the controller to it.'
                    : 'One key covers a whole Collective. If its probes serve more than one tank, link the others below once it is connected.'}
                </small>
              </label>
            )}
            {tanks && tanks.length === 1 && !asking && (
              <div className="hk-one-tank">
                <p className="ts-hint" style={{ margin: 0 }}>
                  Readings go to <strong>{tanks[0].name}</strong>.
                </p>
                <button
                  type="button"
                  className="hk-btn"
                  onClick={() => {
                    setOtherAquarium(true);
                    setTankId('');
                  }}
                >
                  It is for another aquarium
                </button>
              </div>
            )}
            <label className="hk-field hk-field-short">
              <span>Name (optional)</span>
              <input
                type="text"
                maxLength={60}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Display tank"
              />
            </label>
            <div className="hk-actions">
              <button
                type="button"
                className="hk-btn hk-btn-primary"
                onClick={add}
                disabled={busy === 'add' || tanks === null}
              >
                {busy === 'add' ? 'Checking the key…' : 'Add key'}
              </button>
              <button
                type="button"
                className="hk-btn"
                onClick={() => {
                  setOpen(false);
                  setErr('');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="ts-add-btn hk-add" onClick={() => setOpen(true)}>
            {keys.length ? 'Add another key' : 'Add a HYDROS key'}
          </button>
        ))}
    </div>
  );
}

// Per-row tank picker + unlink, listing every controller on the user's
// HYDROS account. Users with multiple tanks set assignments from one
// place instead of visiting each tank's Equipment settings. The PUT
// endpoint refuses to link a controller already on another tank, so a
// "move" is two calls: DELETE the old tank's link, then PUT the new
// one — done sequentially with a single Move button.
function HydrosControllersList({ account, onChanged, linkTo = null }) {
  const confirm = useConfirm();
  const [tanks, setTanks] = useState(null); // null = loading
  const [pending, setPending] = useState({}); // thing_id → in-flight tank pick
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');

  // Reloaded with the account, so a tank's "reads ... now" follows a link
  // just made.
  useEffect(() => {
    api('/api/tanks')
      // Only the keeper's own tanks: a tank shared with them is its
      // owner's to link.
      .then((list) =>
        setTanks(Array.isArray(list) ? list.filter((t) => !t.role || t.role === 'owner') : [])
      )
      .catch(() => setTanks([]));
  }, [account]);

  const controllers = account?.controllers || [];
  const names = new Map(controllers.map((c) => [c.thing_id, c.name]));
  if (!controllers.length) {
    return (
      <p className="ts-hint" style={{ marginTop: 10 }}>
        No controllers found on this HYDROS account yet. If you just added one, give it a minute and
        reopen this sheet.
      </p>
    );
  }

  // A tank reads one controller. When the target already reads another,
  // the server refuses until the keeper says to replace it. Returns the
  // link answer, or false when they keep the one it has.
  const linkTank = async (tankId, c) => {
    const put = (replace) =>
      api(`/api/tanks/${tankId}/hydros`, {
        method: 'PUT',
        body: replace ? { thing_id: c.thing_id, replace: true } : { thing_id: c.thing_id },
      });
    try {
      return (await put(false)) || {};
    } catch (e) {
      if (e.payload?.error !== 'tank_has_controller') throw e;
      const tankName = (tanks || []).find((t) => t.id === tankId)?.name || 'This tank';
      const ok = await confirm({
        title: `Switch ${tankName} to ${c.name}?`,
        body: `${tankName} reads ${e.payload.current_name} now. Switching stops those readings and clears its probe picks. Readings already logged stay.`,
        confirmLabel: 'Switch',
      });
      if (!ok) return false;
      return (await put(true)) || {};
    }
  };

  const moveTo = async (c, targetTankId) => {
    setErr('');
    setBusy(c.thing_id);
    const from = c.assigned_tank_id || null;
    let unlinked = false;
    try {
      if (from) {
        await api(`/api/tanks/${from}/hydros`, { method: 'DELETE' });
        unlinked = true;
      }
      const moved = targetTankId ? await linkTank(targetTankId, c) : true;
      if (!moved && from) {
        await api(`/api/tanks/${from}/hydros`, { method: 'PUT', body: { thing_id: c.thing_id } });
      }
      setPending((p) => ({ ...p, [c.thing_id]: '' }));
      await onChanged();
    } catch (e) {
      // The old tank's link was already cleared: put it back so a failed
      // move never leaves the controller on no tank.
      let restored = !unlinked;
      if (unlinked) {
        try {
          await api(`/api/tanks/${from}/hydros`, { method: 'PUT', body: { thing_id: c.thing_id } });
          restored = true;
        } catch {
          restored = false;
        }
      }
      const why = e.payload?.detail || e.message || 'Could not move the controller.';
      setErr(
        restored ? why : `${why} It is not linked to a tank now: pick one and tap Link again.`
      );
      await onChanged();
    } finally {
      setBusy(null);
    }
  };

  const unlink = async (c) => {
    if (!c.assigned_tank_id) return;
    setErr('');
    setBusy(c.thing_id);
    try {
      await api(`/api/tanks/${c.assigned_tank_id}/hydros`, { method: 'DELETE' });
      await onChanged();
    } catch (e) {
      setErr(e.message || 'Could not unlink.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginTop: 10 }}>
      {err && (
        <div role="alert" className="ts-err">
          {err}
        </div>
      )}
      {note && (
        <p className="hk-done" role="status" style={{ margin: '0 0 12px' }}>
          {note}
        </p>
      )}
      <div
        className="font-heading"
        style={{
          fontSize: 11,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--text-dim)',
          marginBottom: 6,
        }}
      >
        Your controllers
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {controllers.map((c) => {
          if (c.source === 'api') {
            return (
              <KeyControllerRow
                key={c.thing_id}
                c={c}
                tanks={tanks}
                names={names}
                linkPick={linkTo?.thingId === c.thing_id ? linkTo.tankId : null}
                busy={busy === c.thing_id}
                onLink={async (tankId) => {
                  setErr('');
                  setNote('');
                  setBusy(c.thing_id);
                  try {
                    const out = await linkTank(tankId, c);
                    if (out) {
                      const tankName = (tanks || []).find((t) => t.id === tankId)?.name;
                      if (tankName) setNote(linkedNote(tankName, c.name, out));
                      await onChanged();
                    }
                  } catch (e) {
                    setErr(e.payload?.detail || e.message || 'Could not link the controller.');
                  } finally {
                    setBusy(null);
                  }
                }}
                onUnlink={async (tankId) => {
                  setErr('');
                  setBusy(c.thing_id);
                  try {
                    await api(`/api/tanks/${tankId}/hydros`, { method: 'DELETE' });
                    await onChanged();
                  } catch (e) {
                    setErr(e.message || 'Could not unlink.');
                  } finally {
                    setBusy(null);
                  }
                }}
              />
            );
          }
          const pick = pending[c.thing_id] ?? c.assigned_tank_id ?? '';
          const dirty = pick !== (c.assigned_tank_id || '');
          return (
            <li
              key={c.thing_id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 8,
                padding: '8px 0',
                borderTop: '1px solid var(--surface-border)',
              }}
            >
              <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                <div style={{ fontWeight: 500 }}>{c.name}</div>
                <div className="ts-hint">
                  {c.online ? 'Online' : 'Offline'}
                  {c.assigned_tanks?.length > 1
                    ? ` · on ${c.assigned_tanks.map((t) => t.name).join(', ')}`
                    : c.assigned_tank_name && ` · on ${c.assigned_tank_name}`}
                </div>
              </div>
              <select
                value={pick}
                disabled={tanks === null}
                onChange={(e) => setPending((p) => ({ ...p, [c.thing_id]: e.target.value }))}
                style={{ minWidth: 160, flex: '1 1 160px', minHeight: 44 }}
              >
                <option value="">Not linked</option>
                {(tanks || []).map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
              </select>
              {dirty && (
                <button
                  type="button"
                  className="hk-btn hk-btn-primary"
                  onClick={() => moveTo(c, pick || null)}
                  disabled={busy === c.thing_id}
                >
                  {busy === c.thing_id
                    ? '…'
                    : !pick
                      ? 'Unlink'
                      : c.assigned_tank_id
                        ? 'Move'
                        : 'Link'}
                </button>
              )}
              {!dirty && c.assigned_tank_id && (
                <button
                  type="button"
                  className="hk-btn hk-btn-danger"
                  onClick={() => unlink(c)}
                  disabled={busy === c.thing_id}
                >
                  {busy === c.thing_id ? '…' : 'Unlink'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

// A key-connected controller (a whole Collective) can feed several tanks,
// so its row lists every tank it is on, each with its own Unlink, and
// linking it to another tank only ever adds: nothing is unlinked on the way.
function KeyControllerRow({ c, tanks, names, linkPick = null, busy, onLink, onUnlink }) {
  const [pick, setPick] = useState(linkPick || '');
  const on = c.assigned_tanks || [];
  const onIds = new Set(on.map((t) => t.id));
  const free = (tanks || []).filter((t) => !onIds.has(t.id));
  return (
    <li
      style={{
        padding: '8px 0',
        borderTop: '1px solid var(--surface-border)',
      }}
      data-unlinked-controller={on.length ? undefined : ''}
      data-hydros-thing={c.thing_id}
    >
      <div style={{ fontWeight: 500 }}>{c.name}</div>
      <div className="ts-hint">
        {c.online ? 'Online' : 'Offline'}
        {on.length ? '' : ' · not linked to a tank yet'}
      </div>
      {on.map((t) => (
        <div key={t.id} className="hk-row" style={{ borderTop: 'none', padding: '4px 0' }}>
          <div className="hk-row-main">on {t.name}</div>
          <button
            type="button"
            className="hk-btn hk-btn-danger"
            onClick={() => onUnlink(t.id)}
            disabled={busy}
          >
            {busy ? '…' : 'Unlink'}
          </button>
        </div>
      ))}
      {free.length > 0 && (
        <div className="hk-link-row">
          <select
            className="hk-pick-select"
            value={pick}
            onChange={(e) => setPick(e.target.value)}
            aria-label={`Link ${c.name} to a tank`}
          >
            <option value="">{on.length ? 'Also link to…' : 'Link to a tank…'}</option>
            {free.map((t) => (
              <option key={t.id} value={t.id}>
                {t.hydros_thing_id && t.hydros_thing_id !== c.thing_id
                  ? `${t.name} (reads ${names?.get(t.hydros_thing_id) || 'another controller'} now)`
                  : t.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="hk-btn hk-btn-primary"
            disabled={!pick || busy}
            onClick={async () => {
              await onLink(pick);
              setPick('');
            }}
          >
            {busy ? '…' : 'Link'}
          </button>
        </div>
      )}
      {!on.length && tanks !== null && (
        <button
          type="button"
          className="hk-btn hk-add-tank"
          onClick={() => openAddTankForHydros(c.thing_id)}
          disabled={busy}
        >
          Add a tank for it
        </button>
      )}
    </li>
  );
}
